import { CheckCircle, FileText, Target, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { CoverageValidationResultSchema } from '@/services/types/coverage-types';

interface ValidationDetailsProps {
  validation: CoverageValidationResultSchema | null;
}

export const ValidationDetails = ({ validation }: ValidationDetailsProps) => {
  if (!validation) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p>No validation data available</p>
      </div>
    );
  }

  const getSpecificityLevel = (score: number) => {
    if (score >= 0.9)
      return { level: 'Very High', color: 'bg-emerald-100 text-emerald-800' };
    if (score >= 0.7)
      return { level: 'High', color: 'bg-green-100 text-green-800' };
    if (score >= 0.5)
      return { level: 'Medium', color: 'bg-yellow-100 text-yellow-800' };
    if (score >= 0.3)
      return { level: 'Low', color: 'bg-orange-100 text-orange-800' };
    return { level: 'Very Low', color: 'bg-red-100 text-red-800' };
  };

  const getScoreDescription = (score: number) => {
    if (score >= 0.9) {
      return 'Clear mention of carrier, policy number, specific coverage type, and insured/claimant context.';
    }
    if (score >= 0.7) {
      return 'Mentions carrier, policy number, and type, but some ambiguity remains or details are partial.';
    }
    if (score >= 0.5) {
      return 'Clear implication of an insured party AND a carrier name.';
    }
    if (score >= 0.3) {
      return 'Mention of insurance, without implied insured party, or without a carrier name.';
    }
    return 'Vague mention (e.g., "insurance", "a policy") with no carrier or type.';
  };

  const specificityInfo = getSpecificityLevel(
    validation.coverage_specificity_score,
  );

  return (
    <div className="space-y-6">
      {/* Validation Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {validation.is_valid_coverage_mention ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            Coverage Validity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge
              variant="secondary"
              className={
                validation.is_valid_coverage_mention
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }
            >
              {validation.is_valid_coverage_mention
                ? 'Valid Coverage Mention'
                : 'Invalid Coverage Mention'}
            </Badge>
          </div>

          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Validation Criteria:
            </h4>
            <p className="text-sm text-gray-600">
              {validation.is_valid_coverage_mention
                ? 'This mention references a concrete insurance policy instance that could be verified, not just a generic reference to "insurance".'
                : 'This mention appears to be a generic reference to "insurance" rather than a specific policy instance.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Specificity Scoring */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-5 w-5" />
            Coverage Specificity Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Specificity Score:</span>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">
                  {(validation.coverage_specificity_score * 100).toFixed(1)}%
                </span>
                <Badge variant="secondary" className={specificityInfo.color}>
                  {specificityInfo.level}
                </Badge>
              </div>
            </div>

            <Progress
              value={validation.coverage_specificity_score * 100}
              className="h-3"
            />

            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Score Range Description:
              </h4>
              <p className="text-sm text-blue-800">
                {getScoreDescription(validation.coverage_specificity_score)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Reasoning */}
      {validation.specificity_score_reasoning && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Specificity Score Reasoning
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm leading-relaxed">
                {validation.specificity_score_reasoning}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scoring Rubric Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Scoring Rubric Reference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full" />
                  <span className="text-sm font-medium">
                    0.0 - 0.2: Very Low
                  </span>
                </div>
                <p className="text-xs text-gray-600 ml-5">
                  Vague mention with no carrier or type
                </p>

                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-500 rounded-full" />
                  <span className="text-sm font-medium">0.3 - 0.4: Low</span>
                </div>
                <p className="text-xs text-gray-600 ml-5">
                  Insurance mention without insured party or carrier
                </p>

                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                  <span className="text-sm font-medium">0.5 - 0.6: Medium</span>
                </div>
                <p className="text-xs text-gray-600 ml-5">
                  Implies insured party AND carrier name
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full" />
                  <span className="text-sm font-medium">0.7 - 0.8: High</span>
                </div>
                <p className="text-xs text-gray-600 ml-5">
                  Carrier, policy number, type with some ambiguity
                </p>

                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                  <span className="text-sm font-medium">
                    0.9 - 1.0: Very High
                  </span>
                </div>
                <p className="text-xs text-gray-600 ml-5">
                  Clear carrier, policy number, coverage type, insured context
                </p>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-md">
              <p className="text-xs text-blue-800">
                <strong>Current Score:</strong>{' '}
                {(validation.coverage_specificity_score * 100).toFixed(1)}%
                falls in the <strong>{specificityInfo.level}</strong> range
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
